import { Link } from 'react-router-dom';

/* Adult Learners' Week promo landing page (/alw) – Nick's shareable URL.
 *
 * Every CTA here carries the cohort tag explicitly, and the route also
 * stores it via storeSource() so the tag survives detours through nav
 * pages before checkout (see attribution/source.js). Keep the two in
 * sync if the tag ever changes.
 */
// The nick-alw source also switches /pay into the card-less free-30-days
// flow (see PROMO_PAY_FLOWS in routes/Pay.jsx) — no plan param needed,
// the plan step is hidden in that flow.
export const ALW_PAY_HREF = '/pay?s=nick-alw';
export const ALW_SOURCE = 'nick-alw';

export function AlwHero({ t }) {
  return (
    <section className="ssi-alw__hero" style={alwStyles.heroWrap}>
      <div style={alwStyles.heroInner}>
        <div style={alwStyles.kicker}>{t.alwKicker}</div>
        <h1 style={alwStyles.heroH1}>{t.alwTitle}</h1>
        <p style={alwStyles.heroLede}>{t.alwLede}</p>
        <div style={alwStyles.heroActions}>
          <Link to={ALW_PAY_HREF} className="btn btn--primary">{t.alwCta}</Link>
        </div>
        <p style={alwStyles.heroTrust}>{t.alwTrust}</p>
      </div>
    </section>
  );
}

export function AlwWhy({ t }) {
  const cards = [
    { title: t.alwWhy1Title, body: t.alwWhy1Body },
    { title: t.alwWhy2Title, body: t.alwWhy2Body },
    { title: t.alwWhy3Title, body: t.alwWhy3Body },
  ];
  return (
    <section className="ssi-alw__why" style={alwStyles.whyWrap}>
      <div style={alwStyles.whyInner}>
        <div style={alwStyles.kicker}>{t.alwWhyKicker}</div>
        <h2 style={alwStyles.h2}>{t.alwWhyTitle}</h2>
        <div style={alwStyles.whyGrid}>
          {cards.map((c, i) => (
            <div key={i} style={alwStyles.whyCard}>
              <div style={alwStyles.whyNum}>{String(i + 1).padStart(2, '0')}</div>
              <div style={alwStyles.whyTitle}>{c.title}</div>
              <p style={alwStyles.whyBody}>{c.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function AlwOffer({ t }) {
  return (
    <section className="ssi-alw__offer" style={alwStyles.offerWrap}>
      <div style={alwStyles.offerCard}>
        <div style={{...alwStyles.kicker, color: 'var(--gold)'}}>{t.alwOfferKicker}</div>
        <h2 style={alwStyles.offerTitle}>{t.alwOfferTitle}</h2>
        <p style={alwStyles.offerBody}>{t.alwOfferBody}</p>
        <ul style={alwStyles.offerList}>
          <li style={alwStyles.offerItem}>{t.alwOffer1}</li>
          <li style={alwStyles.offerItem}>{t.alwOffer2}</li>
          <li style={alwStyles.offerItem}>{t.alwOffer3}</li>
        </ul>
        {/* White override: the default red .btn--primary disappears against
            the card's brand-red background. */}
        <Link to={ALW_PAY_HREF} className="btn btn--primary" style={{alignSelf: 'flex-start', background: '#fff', color: 'var(--brandRed)'}}>{t.alwOfferCta}</Link>
        <p style={alwStyles.offerFine}>{t.alwOfferFine}</p>
      </div>
    </section>
  );
}

const alwStyles = {
  kicker: { fontSize: 12, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--brandRed)', fontWeight: 700, marginBottom: 14 },
  h2: { fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(32px, 4vw, 44px)', lineHeight: 1.08, letterSpacing: '-.012em', margin: '0 0 28px', textWrap: 'balance' },

  heroWrap: { background: 'var(--offWhite, #FDF5F5)', padding: '88px 32px 84px', borderBottom: '1px solid var(--border)' },
  heroInner: { maxWidth: 880, margin: '0 auto', textAlign: 'center' },
  heroH1: { fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(44px, 6.4vw, 76px)', lineHeight: 1.0, letterSpacing: '-.022em', margin: '0 0 24px', textWrap: 'balance' },
  heroLede: { fontSize: 'clamp(17px, 2vw, 20px)', lineHeight: 1.6, color: 'var(--fg-1)', margin: '0 auto 30px', maxWidth: '54ch', textWrap: 'pretty' },
  heroActions: { display: 'flex', justifyContent: 'center', marginBottom: 16 },
  heroTrust: { fontSize: 14, color: 'var(--grey)', margin: 0 },

  whyWrap: { background: '#fff', padding: '80px 32px' },
  whyInner: { maxWidth: 1080, margin: '0 auto' },
  whyGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 },
  whyCard: { background: 'var(--offWhite, #FDF5F5)', border: '1px solid var(--border)', borderRadius: 16, padding: '26px 24px' },
  whyNum: { fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: 'var(--brandRed)', marginBottom: 12 },
  whyTitle: { fontSize: 18, fontWeight: 700, marginBottom: 10, letterSpacing: '-.008em' },
  whyBody: { fontSize: 15.5, lineHeight: 1.6, color: 'var(--fg-1)', margin: 0, textWrap: 'pretty' },

  offerWrap: { background: '#fff', padding: '8px 32px 96px' },
  offerCard: { maxWidth: 880, margin: '0 auto', background: 'var(--brandRed)', color: '#fff', borderRadius: 20, padding: 'clamp(32px, 5vw, 52px)', display: 'flex', flexDirection: 'column' },
  offerTitle: { fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(28px, 3.6vw, 40px)', lineHeight: 1.08, letterSpacing: '-.012em', margin: '0 0 16px', color: '#fff', textWrap: 'balance' },
  offerBody: { fontSize: 17, lineHeight: 1.6, color: 'rgba(255,255,255,.92)', margin: '0 0 20px', maxWidth: '58ch', textWrap: 'pretty' },
  offerList: { listStyle: 'none', margin: '0 0 28px', padding: 0, display: 'flex', flexDirection: 'column', gap: 10 },
  offerItem: { fontSize: 16, lineHeight: 1.5, color: 'rgba(255,255,255,.92)', paddingLeft: 26, position: 'relative' },
  offerFine: { fontSize: 13.5, color: 'rgba(255,255,255,.75)', margin: '14px 0 0' },
};

/* Tick markers for the offer list – injected once, mirrors PayTickStyleInjector's approach. */
export function AlwTickStyleInjector() {
  return (
    <style>{`
      .ssi-alw__offer li::before {
        content: '✓';
        position: absolute;
        left: 0;
        top: 0;
        font-weight: 700;
        color: var(--gold, #FEC902);
      }
    `}</style>
  );
}
